import React, { useEffect, useMemo } from 'react';
import { Form, Input, Select, Switch, Row, Col } from 'antd';
import type { CompanyBranch } from '../../types/company';
import { withDevModalFilePath, NumericInput, PhoneInput, PostalCodeInput, EmailInput } from '../common';
import { katakanaRule, phoneNumberRule, postalCodeRule } from '@/constants/validation';

const { Modal } = withDevModalFilePath('client/src/components/company/BranchFormModal.tsx');
const { Option } = Select;

type BranchFormValues = Partial<CompanyBranch> & { displayId?: string; parentBranchId?: string | null };

interface BranchFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: BranchFormValues) => void;
  initialValues?: BranchFormValues;
  loading?: boolean;
  /** All branches for parent selection */
  branches?: CompanyBranch[];
  /** Pre-selected parent branch ID (for "Add child" action) */
  defaultParentId?: string | null;
}

const BranchFormModal: React.FC<BranchFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  loading,
  branches = [],
  defaultParentId,
}) => {
  const [form] = Form.useForm();

  // Get available parent branches (exclude self and descendants for edit mode)
  const availableParentBranches = useMemo(() => {
    if (!branches.length) return [];

    const editingId = initialValues?.id;

    // Filter out branches that cannot be parents
    return branches.filter(branch => {
      // Can't be its own parent
      if (editingId && branch.id === editingId) return false;

      // Can't select a descendant as parent (circular reference)
      if (editingId && branch.path?.includes(`/${editingId}/`)) return false;

      // Max 3 levels (0, 1, 2), so level 2 branches can't have children
      if (branch.level >= 2) return false;

      return true;
    });
  }, [branches, initialValues?.id]);

  // Generate hierarchical label with tree lines
  const getBranchLabel = (branch: CompanyBranch): string => {
    const indent = branch.level > 0
      ? '│  '.repeat(branch.level - 1) + '├─ '
      : '';
    return `${indent}${branch.name}`;
  };

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
        // Set default parent if provided (for "Add child" action)
        if (defaultParentId) {
          form.setFieldsValue({ parentBranchId: defaultParentId });
        }
      }
    }
  }, [visible, initialValues, form, defaultParentId]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
      form.resetFields();
    } catch {
      // バリデーションエラーの場合は何もしない
    }
  };

  const prefectures = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
    '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
    '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
    '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
  ];

  const branchTypes = [
    { value: '本社', label: '本社' },
    { value: '支店', label: '支店' },
    { value: '営業所', label: '営業所' },
    { value: '工場', label: '工場' },
    { value: '研究所', label: '研究所' },
    { value: '物流センター', label: '物流センター' },
    { value: 'その他', label: 'その他' },
  ];

  return (
    <Modal
      title={initialValues ? '拠点編集' : '拠点追加'}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      width={720}
      confirmLoading={loading}
      okText={initialValues ? '更新' : '登録'}
      cancelText="キャンセル"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ isActive: true }}
      >
        <Row gutter={16}>
          <Col span={8}>        <Form.Item label="支店ID">
            <Input value={initialValues?.displayId ?? '保存後に自動採番されます'} disabled />
          </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="name"
              label="支店名"
              rules={[
                { required: true, message: '支店名を入力してください' },
                { max: 200, message: '200文字以内で入力してください' },
              ]}
            >
              <Input placeholder="東京支店" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="nameKana"
              label="支店名（カナ）"
              rules={[
                { max: 200, message: '200文字以内で入力してください' },
                katakanaRule(),
              ]}
            >
              <Input placeholder="トウキョウシテン" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="タイプ"
            >
              <Select placeholder="選択してください" allowClear>
                {branchTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="parentBranchId"
              label="親マップ"
              tooltip="最大3階層まで設定できます"
            >
              <Select
                placeholder="なし（ルート）"
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ fontFamily: 'monospace' }}
              >
                {availableParentBranches.map(branch => (
                  <Option key={branch.id} value={branch.id}>
                    {getBranchLabel(branch)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="employeeCount"
              label="従業員数"
            >
              <NumericInput
                min={0}
                style={{ width: '100%' }}
                placeholder="50"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="postalCode"
              label="郵便番号"
              rules={[postalCodeRule('正しい郵便番号を入力してください')]}
            >
              <PostalCodeInput placeholder="100-0001" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="prefecture"
              label="都道府県"
            >
              <Select placeholder="選択してください" allowClear>
                {prefectures.map(pref => (
                  <Option key={pref} value={pref}>{pref}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="city"
              label="市区町村"
            >
              <Input placeholder="千代田区" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="address"
              label="住所"
            >
              <Input placeholder="千代田1-1-1" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="building"
              label="建物名"
            >
              <Input placeholder="サンプル建物5F" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="phone"
              label="電話番号"
              rules={[
                { max: 20, message: '電話番号は20文字以内で入力してください' },
                phoneNumberRule(),
              ]}
            >
              <PhoneInput placeholder="03-1234-5678" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="fax"
              label="FAX番号"
              rules={[
                { max: 20, message: 'FAX番号は20文字以内で入力してください' },
                phoneNumberRule('有効なFAX番号を入力してください'),
              ]}
            >
              <PhoneInput placeholder="03-1234-5679" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="email"
              label="メールアドレス"
              rules={[
                { type: 'email', message: '正しいメールアドレスを入力してください' },
              ]}
            >
              <EmailInput placeholder="branch@example.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="managerName"
              label="管理者名"
            >
              <Input placeholder="山田 太郎" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="managerEmail"
              label="管理者メールアドレス"
              rules={[
                { type: 'email', message: '正しいメールアドレスを入力してください' },
              ]}
            >
              <EmailInput placeholder="manager@example.com" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="isActive"
          label="ステータス"
          valuePropName="checked"
        >
          <Switch checkedChildren="有効" unCheckedChildren="無効" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BranchFormModal;
